import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  GetLiveOpticsDataFromFileImport,
  GetRVToolsDataFromFileImport,
  SetStateWithImportedFileInfo,
} from './data-ingestion.actions';
import { WhitneyService } from '../../core/services/whitney.service';
import { catchError, Observable, tap } from 'rxjs';
import { HttpEventType } from '@angular/common/http';
import { SaveSupercolliderEvent } from '../project-details/project-details.actions';
import { SUPERCOLLIDER_EVENT_TYPE } from '../../enums/pcsizer';
import { Store } from '@ngxs/store';
import { ProjectDetailsState } from '../project-details/project-details.state';

export interface DataIngestionStateModel {
  fileImported: boolean;
  sourceType: string | undefined;
  importedData: any;
  parsedData: any;
  fileName: string | undefined;
  fileUploadProgress: number | string;
  fileSize: number | undefined;
  liveOpticsProjectId: string | undefined;
}

@State<DataIngestionStateModel>({
  name: 'dataIngestion',
  defaults: {
    fileImported: false,
    sourceType: undefined,
    importedData: {},
    parsedData: {},
    fileName: undefined,
    fileUploadProgress: 0,
    fileSize: 0,
    liveOpticsProjectId: undefined,
  },
})
@Injectable()
export class DataIngestionState {
  constructor(private whitneyService: WhitneyService, private store: Store) {}

  @Selector()
  static getFileImportedDataFromState(state: DataIngestionStateModel) {
    return state?.importedData;
  }

  @Selector()
  static getFileImportStatus(state: DataIngestionStateModel): number | string {
    return state?.fileUploadProgress;
  }

  @Selector()
  static getDataIngestionState(state: DataIngestionStateModel) {
    return state;
  }

  @Action(GetLiveOpticsDataFromFileImport)
  getLiveOpticsFileConfig(
    ctx: StateContext<DataIngestionStateModel>,
    action: GetLiveOpticsDataFromFileImport
  ) {
    return this.whitneyService.getLiveOpticsFileConfig(action.file).pipe(
      tap((event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          const progress = Math.round((100 * event.loaded) / event.total);
          ctx.patchState({
            fileUploadProgress: progress,
          });
        } else if (event.type === HttpEventType.Response) {
          ctx.patchState({
            importedData: event.body,
          });
          // Track successful LiveOptics import
          this.trackDataImportEvent(SUPERCOLLIDER_EVENT_TYPE.LIVEOPTICS_IMPORT_SUCCESS);
        }
      }),
      catchError((error) => {
        // Track failed LiveOptics import
        this.trackDataImportEvent(SUPERCOLLIDER_EVENT_TYPE.LIVEOPTICS_IMPORT_FAILURE);
        throw error;
      })
    );
  }

  @Action(GetRVToolsDataFromFileImport)
  getRVToolsFileConfig(
    ctx: StateContext<DataIngestionStateModel>,
    action: GetRVToolsDataFromFileImport
  ) {
    return this.whitneyService.getRVToolsFileConfig(action.file).pipe(
      tap((event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          const progress = Math.round((100 * event.loaded) / event.total);
          ctx.patchState({
            fileUploadProgress: progress,
          });
        } else if (event.type === HttpEventType.Response) {
          ctx.patchState({
            importedData: event.body,
          });
          // Track successful RV Tools import
          this.trackDataImportEvent(SUPERCOLLIDER_EVENT_TYPE.RVTOOLS_IMPORT_SUCCESS);
        }
      }),
      catchError((error) => {
        // Track failed RV Tools import
        this.trackDataImportEvent(SUPERCOLLIDER_EVENT_TYPE.RVTOOLS_IMPORT_FAILURE);
        throw error;
      })
    );
  }

  @Action(SetStateWithImportedFileInfo)
  setStateWithImportedFileInfo(
    ctx: StateContext<DataIngestionStateModel>,
    action: SetStateWithImportedFileInfo
  ) {
    ctx.patchState({
      fileImported: action.fileImported,
      sourceType: action.selectedSource,
      fileName: action.fileName,
      parsedData: action.parsedData,
      fileSize: action.fileSize,
      liveOpticsProjectId: action.liveOpticsProjectId,
      importedData: {}
    });
  }

  private trackDataImportEvent(eventType: string) {
    // Get current project ID from project details state
    const currentProject = this.store.selectSnapshot(ProjectDetailsState.getCurrentProject);
    if (currentProject?.projectId) {
      this.store.dispatch(new SaveSupercolliderEvent(
        currentProject.projectId.toString(),
        eventType
      ));
    }
  }
}
